/**
 * Sticky notes take a colour name (yellow, blue, pink, green, gray) that maps
 * to a CSS variable with light and dark variants. Partnr often sends hex
 * instead, which StickyNote turns into `hsl(var(--sticky-#FEF3C7))`: an
 * invalid colour, so the note renders with no background. Normalise here.
 */

export const STICKY_COLOR_NAMES = ['yellow', 'blue', 'pink', 'green', 'gray'] as const;
export type StickyColorName = (typeof STICKY_COLOR_NAMES)[number];

/** Closest sticky colour name for a name or hex value. Unknown input falls back to yellow. */
export function toStickyColorName(color: string | undefined | null): StickyColorName {
  if (!color) return 'yellow';
  const value = color.trim().toLowerCase();
  if ((STICKY_COLOR_NAMES as readonly string[]).includes(value)) return value as StickyColorName;
  if (value === 'grey') return 'gray';
  if (value === 'purple' || value === 'violet' || value === 'indigo') return 'blue';
  if (value === 'red' || value === 'rose' || value === 'orange') return 'pink';

  const hex = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/)?.[1];
  if (!hex) return 'yellow';
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  if (saturation < 0.15) return 'gray';

  let hue: number;
  if (max === r) hue = 60 * (((g - b) / delta) % 6);
  else if (max === g) hue = 60 * ((b - r) / delta + 2);
  else hue = 60 * ((r - g) / delta + 4);
  if (hue < 0) hue += 360;

  if (hue < 20 || hue >= 290) return 'pink';
  if (hue < 70) return 'yellow';
  if (hue < 170) return 'green';
  return 'blue';
}

/** Content fields for a sticky colour; clears any custom colour left from text detection. */
export function stickyColorFields(color: string | undefined | null): Record<string, unknown> {
  return { color: toStickyColorName(color), customColor: null, colorCode: null };
}
